import * as types from 'constants/subscriptionSearch';
import { setLoadingUnique } from 'actions';
import { trackDecisionTreeSubscriptionSearch } from '../../analytics/searchEventAnalytics';
import * as orderSearchTypes from 'constants/ordersSearchActionTypes';
import { getSelectedSubscriptionList, selectSubscriptionSystem } from '../../selectors/tools';
import { SUBSCRIPTION_SYSTEMS } from 'constants/integrations';
import { cancelSubscriptionsFromDecisionTreeViaSimplrOrder } from 'actions/tools/SimplrOrderSubscriptionActions';
import { showErrorMessage } from '../../util/errorPopupUtil';
import makeRequest from '../../api/makeRequest';
import environment from 'environment';
import {
  extractSelectedTicketIdFromState,
  getSimplrAccountIdOfSelectedTicket,
  getSubscriptionSearchParams,
} from '../../extractFromState';
import { COMMON_SUBSCRIPTION_EMAIL } from '../../constants/searchActions';
import { handleError } from '../../services/ErrorService/ErrorService';
import isEmpty from 'lodash/isEmpty';

export const setSearchOrders = (orders) => ({
  type: orderSearchTypes.SET_ORDERS,
  orders,
});

export const selectSubscription = (subscription) => ({
  type: types.SELECT_SUBSCRIPTION,
  subscription,
});

export const selectSubscriptionInDecisionTree = (subscriptionId) => ({
  type: types.SELECT_SUBSCRIPTION_IN_DECISION_TREE,
  subscriptionId,
});

export const unSelectSubscriptionInDecisionTree = (subscriptionId) => ({
  type: types.UNSELECT_SUBSCRIPTION_IN_DECISION_TREE,
  subscriptionId,
});

export const unSelectAllSubscriptionsInDecisionTree = () => ({
  type: types.UNSELECT_ALL_SUBSCRIPTIONS_IN_DECISION_TREE,
});

export const setSelectedSubscriptionForConfirmationPage = (selectedSubscriptionForConfirmationPage) => ({
  type: types.SET_SELECTED_SUBSCRIPTION_FOR_CONFIRMATION_PAGE,
  selectedSubscriptionForConfirmationPage,
});

export const updateSubscription = (subscription) => ({
  type: types.UPDATE_SUBSCRIPTION,
  subscription,
});

export const setPauseSubscription = (pauseSubscriptionDetails) => ({
  type: types.SET_PAUSE_SUBSCRIPTION,
  pauseSubscriptionDetails,
});

export const selectSubscriptionTask = (selectedTask) => ({
  type: types.SELECT_SUBSCRIPTION_TASK,
  selectedTask,
});

export const setSubscriptions = (subscriptions) => ({
  type: types.SET_SUBSCRIPTIONS,
  subscriptions,
});

export const setSubscriptionSystem = (subscriptionSystem) => ({
  type: types.SET_SUBSCRIPTION_SYSTEM,
  subscriptionSystem,
});

export const setShopContext = (shopContext) => ({
  type: types.SET_SHOP_CONTEXT,
  shopContext,
});

export const setSearchType = (searchType) => ({
  type: types.SET_SEARCH_TYPE,
  searchType,
});

export const setSearchValue = (searchValue) => ({
  type: types.SET_SEARCH_VALUE,
  searchValue,
});

export const cancelSubscriptionsFromDecisionTree =
  ({ cancelNonShippedOrders = false, cancellationReason, cancellationNote }) =>
  async (dispatch, getState) => {
    const state = getState();
    const subscriptionSystem = selectSubscriptionSystem(state);
    switch (subscriptionSystem) {
      case SUBSCRIPTION_SYSTEMS.SIMPLR_ORDER:
        return await dispatch(cancelSubscriptionsFromDecisionTreeViaSimplrOrder(cancellationReason, cancellationNote));
      case SUBSCRIPTION_SYSTEMS.COMMON:
        return await dispatch(cancelSubscriptions(cancelNonShippedOrders));
      default:
        throw new Error('Ordering system is not supported');
    }
  };

export const lastOrderOfSubscriptionIsFulfilled = (subscriptionSystem) => async (dispatch) => {
  switch (subscriptionSystem) {
    case SUBSCRIPTION_SYSTEMS.SIMPLR_ORDER:
      // Manscaped API is not providing us info about subscription orders for now,
      // so Jason and Semmi decided that for now lets assume that orders are always fullfilled
      // TODO: Manscaped will provide us Orders API and we will be able to check if order was fullfilled
      return true;
    default:
      showErrorMessage(`Sorry, subscription system ${subscriptionSystem} is not found.`);
  }

  return false;
};

export const searchCommonSubscriptions = (searchValue) => async (dispatch, getState) => {
  try {
    dispatch(setLoadingUnique(true, 'searchPage'));
    const state = getState();
    const searchParams = getSubscriptionSearchParams(state);
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const params = {
      simplrAccountId,
      ...(searchParams.searchType === COMMON_SUBSCRIPTION_EMAIL && {
        customerEmail: searchValue,
      }),
    };
    trackDecisionTreeSubscriptionSearch(params);
    const { subscriptions } = await makeRequest(`${environment.eCommerce.SUBSCRIPTIONS_BASE_URL}`, {
      params,
    });

    dispatch(setSubscriptions(subscriptions));
  } catch (error) {
    handleError({ error, customMessage: 'Search failed', showPopup: true });
  } finally {
    dispatch(setLoadingUnique(false, 'searchPage'));
  }
};

export const cancelSubscriptions =
  (cancelNonShippedOrders = false) =>
  async (dispatch, getState) => {
    const state = getState();
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const selectedSubscriptions = getSelectedSubscriptionList(state);
    const conversationId = extractSelectedTicketIdFromState(state);

    if (isEmpty(selectedSubscriptions)) {
      throw new Error('No selected subscription');
    }

    await Promise.all(
      selectedSubscriptions.map((subscription) => {
        const data = {
          id: subscription.id,
          simplrAccountId,
          conversationId,
          cancelNonShippedOrders,
        };

        return makeRequest(`${environment.eCommerce.SUBSCRIPTIONS_BASE_URL}/cancel`, {
          method: 'post',
          data,
        });
      })
    );
    dispatch(setSubscriptions([]));
  };
